import { useState } from 'react';
import { useNavigate } from 'react-router';

import { documentMachine } from '@/config/documentStates';
import { useNotificationModal } from '@/hooks/useNotificationModal';
import { getUserInfo } from '@/lib/auth';
import { apiService } from '@/lib/axios';
import { startNewRevision } from '@/lib/documentFlow';

/**
 * Helper: transforms an appendix row value into a base64-safe format.
 * Extracted to avoid repetition in payload builders.
 */
function normalizeAppendixValue(item) {
  return {
    ...item,
    value: item?.value?.base64 ? item.value.base64 : item?.value,
  };
}

function extractApprovalSubs(value) {
  const rows = Array.isArray(value) ? value : value ? [value] : [];

  return rows.map((row) => row?.value?.sub || row?.sub).filter(Boolean);
}

/**
 * Transforms complex form fields (clause, appendix, references, terms, responsibilities)
 * from nested UI structure to flat database format.
 * Used by both create and update code paths.
 */
function transformDocumentFields(form) {
  return {
    // Clause sections (may contain appendix items)
    clause:
      form.clause?.clauses?.map(({ formData, rows, title }) => ({
        title,
        value: rows?.map(({ id }) => normalizeAppendixValue(formData?.[id])),
      })) ?? [],
    // Appendix attachments
    appendix:
      form.appendix?.rows?.map(({ id }) =>
        normalizeAppendixValue(form.appendix?.formData?.[id])
      ) ?? [],
    // Normalised references
    references: form.references
      ? Object.values(form.references)
          .sort((a, b) => a.order - b.order)
          .map(({ value }) => ({ serial: value?.number, title: value?.title }))
      : [],
    // Term definitions
    terms: form.terms
      ? [...form.terms]
          .sort((a, b) => a.order - b.order)
          .map(({ value }) => value)
      : [],
    // Responsibilities
    responsibilities:
      form.responsibilities?.rows?.map(({ id }) => {
        const item = form.responsibilities.formData?.[id];
        return {
          title: item?.title,
          value: item?.value
            ? Object.values(item.value)
                .sort((a, b) => a.order - b.order)
                .map(({ value }) => value)
            : [],
        };
      }) ?? [],
  };
}

/**
 * Transforms the form state into the payload expected by the create API.
 */
function buildCreatePayload(form) {
  const creator = getUserInfo()?.sub;
  if (!creator) {
    throw new Error('Unable to identify current user. Please log in again.');
  }

  // Build reviewer pipeline from form approvals
  const reviewers = {
    qmrQa: extractApprovalSubs(form.approvalsAssigned),
    processOwners: extractApprovalSubs(form.approvalsOwner),
    managementApprovers: extractApprovalSubs(form.approvalsPersonnel),
  };
  const reviewerPipeline = documentMachine.buildPipeline(reviewers);

  // Transform complex fields from nested form structure to flat DB format
  const { clause, appendix, references, terms, responsibilities } =
    transformDocumentFields(form);

  return {
    creator,
    documentNumber: form.documentNumber,
    documentTitle: form.documentTitle,
    companyName: form.companyName,
    companyLogo: form.companyLogo?.base64,
    division: form.division,
    owner: form.owner,
    objective: form.objective,
    scope: form.scope,
    templateId: form.templateId,
    reviewers,
    reviewerPipeline,
    clause,
    appendix,
    references,
    terms,
    responsibilities,
  };
}

/**
 * Extracts reviewers from form approvals state.
 * Used consistently across update, submit, and other actions.
 */
function extractReviewers(form) {
  return {
    qmrQa: extractApprovalSubs(form.approvalsAssigned),
    processOwners: extractApprovalSubs(form.approvalsOwner),
    managementApprovers: extractApprovalSubs(form.approvalsPersonnel),
  };
}

const REQUIRED_APPROVAL_GROUPS = [
  ['qmrQa', 'QMR/QA reviewer'],
  ['processOwners', 'Process Owner reviewer'],
  ['managementApprovers', 'Management approver'],
];

function getMissingApprovalGroups(reviewers) {
  return REQUIRED_APPROVAL_GROUPS.filter(
    ([key]) => !reviewers?.[key]?.length
  ).map(([, label]) => label);
}

/**
 * Master orchestrator for all document state-transition actions.
 *
 * Replaces the embedded action logic in ActionsPanel.
 *
 * @param {Object} opts
 * @param {string} opts.documentId - Document UUID (null for new documents)
 * @param {Object} opts.documentData - The resolved display document
 * @param {Object} opts.template - Template metadata (needed for create)
 * @param {Array<Object>} opts.reviews - Review comments from SectionTabs
 * @returns {Object} { allowedActions, rejectReason, setRejectReason, dialogs, actions }
 */
export const useDocumentActions = ({
  documentId,
  documentData,
  form,
  reviews,
  periodReviewYears,
}) => {
  const navigate = useNavigate();
  const { showNotificationModal } = useNotificationModal();

  // ── Dialog state (extracted from component) ─────────────────
  const [rejectReason, setRejectReason] = useState('');
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isReturnDialogOpen, setIsReturnDialogOpen] = useState(false);

  // ── Action eligibility ──────────────────────────────────────
  const userInfo = getUserInfo();
  const { sub: userSub } = userInfo || {};
  const docStatus = documentData?.status;
  const reviewerPipeline = documentData?.reviewerPipeline || [];
  const currentActorIndex = documentData?.currentActorIndex ?? 0;
  const creatorSub = documentData?.creator;

  const allowedActions = docStatus
    ? documentMachine.getActions(
        docStatus,
        userSub,
        reviewerPipeline,
        currentActorIndex,
        creatorSub,
        userInfo?.['cognito:groups']
      )
    : [];

  // ── CREATE (new doc → REVIEWING) ────────────────────────────
  const createDocument = async () => {
    try {
      const payload = buildCreatePayload(form);

      const missingApprovalGroups = getMissingApprovalGroups(payload.reviewers);
      if (missingApprovalGroups.length) {
        showNotificationModal(
          'Cannot Submit',
          `Please assign at least one ${missingApprovalGroups.join(', ')} before submitting.`,
          'error'
        );
        return;
      }

      const response = await apiService.documents.submitNew(payload);
      if (response.data.success) {
        navigate('/documents', { state: 'pending' });

        showNotificationModal(
          'Submitted',
          `Document submitted successfully. Document number: ${response.data.data?.serial || response.data.data?.id}`,
          'success'
        );
      } else {
        throw new Error(response.data.error || 'Failed to submit document');
      }
    } catch (error) {
      showNotificationModal(
        'Submit Failed',
        error.response?.data?.error || error.message,
        'error'
      );
    }
  };

  // ── SAVE (draft) ─────────────────────────────────────────────
  const createDraft = async () => {
    try {
      const payload = buildCreatePayload(form);
      const response = await apiService.documents.create({
        ...payload,
        isDraft: true,
      });
      if (response.data.success) {
        showNotificationModal('Saved', 'Document saved as draft.', 'success');
        navigate('/documents', { state: 'pending' });
      } else {
        throw new Error(response.data.error || 'Failed to save document');
      }
    } catch (error) {
      showNotificationModal(
        'Save Failed',
        error.response?.data?.error || error.message,
        'error'
      );
    }
  };

  const deleteDraft = async () => {
    try {
      const response = await apiService.documents.delete(documentId);

      if (response.data.success) {
        navigate('/documents', { state: 'pending' });
        showNotificationModal(
          'Draft Deleted',
          'Document draft deleted successfully.',
          'success'
        );
      } else {
        throw new Error(
          response.data.error || 'Failed to delete document draft'
        );
      }
    } catch (error) {
      showNotificationModal(
        'Delete Failed',
        error.response?.data?.error ||
          error.message ||
          'Failed to delete document draft',
        'error'
      );
    }
  };

  // ── UPDATE (edit existing document) ──────────────────────────
  const updateDocument = async () => {
    try {
      const reviewers = extractReviewers(form);
      const pipeline = documentMachine.buildPipeline(reviewers);

      // Transform complex fields from nested form structure to flat DB format
      const { clause, appendix, references, terms, responsibilities } =
        transformDocumentFields(form);

      const payload = {
        documentTitle: form.documentTitle || documentData.documentTitle,
        documentNumber: form.documentNumber || documentData.documentNumber,
        companyName: form.companyName || documentData.companyName,
        companyLogo: form.companyLogo || documentData.companyLogo,
        division: form.division || documentData.division,
        owner: form.owner || documentData.owner,
        objective: form.objective || documentData.objective,
        scope: form.scope || documentData.scope,
        templateId: form.templateId || documentData.templateId,
        clause,
        appendix,
        references,
        terms,
        responsibilities,
        reviewers,
        reviewerPipeline: pipeline,
      };
      const response = await apiService.documents.update(documentId, payload);
      if (response.data.success) {
        showNotificationModal('Saved', 'Document saved as draft.', 'success');
        navigate(`/documents`, { state: 'pending' });
      } else {
        throw new Error(response.data.error || 'Failed to save document');
      }
    } catch (error) {
      showNotificationModal(
        'Save Failed',
        error.response?.data?.error || error.message,
        'error'
      );
    }
  };

  // ── SUBMIT ──────────────────────────────────────────────────
  const handleSubmit = async () => {
    const reviewers = extractReviewers(form);
    const pipeline = documentMachine.buildPipeline(reviewers);
    const { clause, appendix, references, terms, responsibilities } =
      transformDocumentFields(form);

    const missingApprovalGroups = getMissingApprovalGroups(reviewers);
    if (missingApprovalGroups.length) {
      showNotificationModal(
        'Cannot Submit',
        `Please assign at least one ${missingApprovalGroups.join(', ')} before submitting.`,
        'error'
      );
      return;
    }

    try {
      const { clause, appendix, references, terms, responsibilities } =
        transformDocumentFields(form);

      const response = await apiService.documents.submit(documentId, {
        documentTitle: form.documentTitle || documentData.documentTitle,
        documentNumber: form.documentNumber || documentData.documentNumber,
        companyName: form.companyName || documentData.companyName,
        companyLogo: form.companyLogo || documentData.companyLogo,
        division: form.division || documentData.division,
        owner: form.owner || documentData.owner,
        objective: form.objective || documentData.objective,
        scope: form.scope || documentData.scope,
        templateId: form.templateId || documentData.templateId,
        appendix,
        clause,
        references,
        responsibilities,
        terms,
        reviewers,
        reviewerPipeline: pipeline,
      });
      if (response.data.success) {
        showNotificationModal(
          'Submitted',
          'Document submitted for review.',
          'success'
        );
        navigate('/documents');
      }
    } catch (error) {
      showNotificationModal(
        'Submit Failed',
        error.response?.data?.error || error.message,
        'error'
      );
    }
  };

  // ── APPROVE ─────────────────────────────────────────────────
  const handleApprove = async () => {
    try {
      const resolved = documentMachine.resolve(
        docStatus,
        'approve',
        currentActorIndex,
        reviewerPipeline.length
      );
      const response = await apiService.documents.approve(documentId, {
        ...documentData,
        ...resolved,
        reviewerPipeline,
        periodReviewYears: periodReviewYears ?? 3,
      });
      if (response.data.success) {
        showNotificationModal('Approved', 'Document approved.', 'success');
        setIsApproveDialogOpen(false);
        navigate('/documents');
      }
    } catch (error) {
      showNotificationModal(
        'Approve Failed',
        error.response?.data?.error || error.message,
        'error'
      );
    }
  };

  // ── REJECT ──────────────────────────────────────────────────
  // Bug fix: rejectReason is now included in the API payload
  const handleReject = async () => {
    try {
      const response = await apiService.documents.reject(documentId, {
        ...documentData,
        reviewerPipeline,
        reason: rejectReason,
        rejectedBy: userSub,
      });
      if (response.data.success) {
        showNotificationModal(
          'Rejected',
          'Document rejected and returned to draft.',
          'info'
        );
        setIsRejectDialogOpen(false);
        setRejectReason('');
        navigate('/documents');
      }
    } catch (error) {
      showNotificationModal(
        'Reject Failed',
        error.response?.data?.error || error.message,
        'error'
      );
    }
  };

  // ── RETURN TO REQUESTOR ─────────────────────────────────────
  const handleReturnToRequestor = async () => {
    try {
      const { sub, name } = getUserInfo();

      // 1. Persist each comment to DB
      for (const { tab, text, entityId: commentEntityId } of reviews) {
        if (commentEntityId) continue;

        await apiService.comments.create({
          entityType: 'Document',
          entityId: documentId,
          entityRevision: documentData.revision,
          tab,
          text,
          authorSub: sub,
          authorName: name,
        });
      }

      // 2. Freeze all comments for this document
      await apiService.comments.bulkFreeze({
        entityType: 'Document',
        entityId: documentId,
        entityRevision: documentData.revision,
      });

      // 3. Return document (status change only)
      const response = await apiService.documents.returnToRequestor(
        documentId,
        {
          ...documentData,
          comments: reviews,
          reviewerPipeline,
        }
      );
      if (response.data.success) {
        showNotificationModal(
          'Returned',
          'Comments sent to requestor.',
          'info'
        );
        setIsReturnDialogOpen(false);
        navigate('/documents');
      }
    } catch (error) {
      showNotificationModal(
        'Return Failed',
        error.response?.data?.error || error.message,
        'error'
      );
    }
  };

  // ── NEW REVISION ────────────────────────────────────────────
  const handleNewRevision = () =>
    startNewRevision(documentId, {
      navigate,
      notify: showNotificationModal,
    });

  return {
    allowedActions,
    rejectReason,
    setRejectReason,
    dialogs: {
      isApproveDialogOpen,
      setIsApproveDialogOpen,
      isRejectDialogOpen,
      setIsRejectDialogOpen,
      isReturnDialogOpen,
      setIsReturnDialogOpen,
    },
    actions: {
      createDocument,
      createDraft,
      deleteDraft,
      submitDraft: handleSubmit,
      update: updateDocument,
      submit: handleSubmit,
      approve: handleApprove,
      reject: handleReject,
      returnToRequestor: handleReturnToRequestor,
      newRevision: handleNewRevision,
    },
  };
};

export default useDocumentActions;
